import { useMemo, memo } from 'react';
import { MultiSelect } from '@/shadcn/multi-select';
import { cn } from '@/shadcn/lib/utils';
import { LabeledOption } from '@/components/ui/widget/views/text/editors/select';

type MultiselectInputProps = {
  classes?: Record<string, string>;
  data: Array<string>;
  options: LabeledOption[] | string[] | undefined;
  onChange: (values: Array<string>) => void;
  placeholder?: string;
  className?: string;
  header?: string;
  disabled?: boolean;
};

// simple multiselect input
// if no header (inline mode) display an info on the nb of selected items, with a tooltip with options on click
// else (batch form mode) display an expandable inline list
// if disabed display a static list with teal markers for selected items, instead of checkboxes
const MultiselectInput = memo(
  ({
    data,
    options = [],
    onChange,
    placeholder,
    className = '',
    header = '',
    disabled = false
  }: MultiselectInputProps): JSX.Element => {
    // focus state
    const isInBatchEdit = useMemo(() => !!header, [header]);

    // handle list of strings as well as list of objects {value,label} for options
    const multiselectOptions = useMemo(
      () =>
        options.map(option => {
          if (typeof option === 'string') {
            return { label: option, value: option };
          }
          return option;
        }),
      [options]
    );

    return (
      <div
        className={cn(
          'flex flex-col gap-2',
          !isInBatchEdit &&
            'ml-[0.2rem] h-[calc(100%-0.5rem)] w-[calc(100%-0.2rem)]'
        )}
      >
        <MultiSelect
          options={multiselectOptions}
          onValueChange={onChange}
          defaultValue={data}
          placeholder={placeholder}
          maxCount={0}
          disabled={disabled}
          className={cn(
            'hover:border-input hover:bg-card size-full border-transparent bg-transparent',
            className,
            isInBatchEdit && 'border-input'
          )}
        />
      </div>
    );
  }
);

export default MultiselectInput;
